/* Techniques home hero: wipe transitions between slides, progress tabs, scroll drift. */
(() => {
  document.querySelectorAll('.tc-hero').forEach((hero) => {
    const slides = [...hero.querySelectorAll('[data-tc-slide]')];
    const texts = [...hero.querySelectorAll('[data-tc-text]')];
    const tabs = [...hero.querySelectorAll('[data-tc-tab]')];
    const stage = hero.querySelector('[data-tc-hero-stage]');
    const copy = hero.querySelector('[data-tc-hero-copy]');
    if (!slides.length) return;
    const tabsWrap = hero.querySelector('.tc-hero__controls');
    const pauseButton = hero.querySelector('[data-tc-hero-pause]');
    const motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    let reduce = motionQuery.matches || hero.dataset.tcMotion === 'false';
    const autoplay = hero.dataset.tcAutoplaySlides !== 'false';
    const saveData = navigator.connection && navigator.connection.saveData;
    const mobile = () => matchMedia('(max-width: 749px)').matches;
    const dur = parseFloat(getComputedStyle(hero).getPropertyValue('--dur')) * 1000 || 7000;
    const timing = getComputedStyle(hero);
    const transitionMs = parseFloat(timing.getPropertyValue('--tc-hero-transition')) * 1000 || 900;
    const textMs = parseFloat(timing.getPropertyValue('--tc-hero-text-delay')) * 1000 || 320;
    let index = 0, timer = null, busy = false, visible = true;
    let queued = null, finishTransition = null, userPaused = false, focusWithin = false;
    let remaining = dur, deadline = 0;

    const videoFor = (slide) => slide.querySelector(mobile() ? 'video.tc-hero__m' : 'video.tc-hero__d');
    // Later slides draw their still (poster or image) on a canvas, cropped like object-fit: cover, so a slide
    // wiping in is never counted as the page's largest paint (see the section). Redrawn on resize.
    const pick = (c) => {
      const m = mobile();
      const url = (m ? c.dataset.stillM : c.dataset.stillD) || c.dataset.stillD || c.dataset.stillM;
      const widths = ((m ? c.dataset.widthsM : c.dataset.widthsD) || '').split(',').map(Number).filter(Boolean);
      if (!url || !widths.length || !/[?&]width=\d+/.test(url)) return url;
      const need = c.clientWidth * (window.devicePixelRatio || 1);
      const w = widths.find((x) => x >= need) || widths[widths.length - 1];
      return url.replace(/([?&]width=)\d+/, `$1${w}`);
    };
    const draw = (c) => {
      const url = pick(c);
      if (!url) return;
      const paint = (im) => {
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        const w = Math.round(c.clientWidth * dpr), h = Math.round(c.clientHeight * dpr);
        if (!w || !h || !im.naturalWidth || c._tcDrawn === `${url} ${w}x${h}`) return;
        c.width = w; c.height = h;
        const k = Math.max(w / im.naturalWidth, h / im.naturalHeight);
        const dw = im.naturalWidth * k, dh = im.naturalHeight * k;
        const ctx = c.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(im, (w - dw) / 2, (h - dh) / 2, dw, dh);
        c._tcDrawn = `${url} ${w}x${h}`;
      };
      if (c._tcImg && c._tcUrl === url) { if (c._tcImg.complete) paint(c._tcImg); return; }
      const im = new Image();
      im.decoding = 'async';
      im.onload = () => paint(im);
      im.src = url;
      c._tcImg = im; c._tcUrl = url;
    };
    const fill = (slide) => slide.querySelectorAll('canvas.tc-hero__canvas').forEach(draw);
    let rz = null;
    window.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(() => hero.querySelectorAll('canvas.tc-hero__canvas').forEach((c) => { if (c._tcImg) draw(c); }), 200); });
    const load = (slide) => {
      fill(slide);
      const v = videoFor(slide);
      if (!v || saveData || reduce) return null;
      if (!v.getAttribute('src') && v.dataset.src) { v.src = v.dataset.src; v.preload = 'auto'; }
      return v;
    };
    let ready = false;
    const play = (slide) => {
      if (reduce || userPaused || focusWithin || !visible || document.hidden || (slide === slides[0] && !ready)) return;
      const v = load(slide);
      if (v) v.play().catch(() => {});
    };
    const pause = (slide) => slide.querySelectorAll('video').forEach((v) => v.pause());

    // Prepare the later slides without competing with the first paint: their stills (small images) 4 s in or
    // once the page has loaded; the next slide's video 2.5 s after the first still has loaded (as before, when
    // it was 2.5 s after start) or once the page has loaded.
    let stillsDone = false, videoDone = false;
    const warmStills = () => { if (stillsDone) return; stillsDone = true; slides.slice(1).forEach(fill); };
    const warmVideo = () => { warmStills(); if (videoDone) return; videoDone = true; if (slides[1]) load(slides[1]); };
    if (slides.length > 1) {
      if (document.readyState === 'complete') setTimeout(warmVideo, 1500);
      else window.addEventListener('load', () => setTimeout(warmVideo, 1500), { once: true });
      setTimeout(warmStills, 4000);
    }

    // Start the first video as soon as its still has loaded (the still shows until the video can play).
    const still = slides[0] && slides[0].querySelector('.tc-hero__still img');
    const start = () => {
      if (ready) return; ready = true;
      if (visible && index === 0) play(slides[0]);
      if (slides.length > 1) setTimeout(warmVideo, 2500);
    };
    if (still && !still.complete) { still.addEventListener('load', start, { once: true }); still.addEventListener('error', start, { once: true }); } else start();

    // A slide that comes up before its video has a frame keeps showing its still for that turn, and the video
    // is revealed once the slide has gone again. Chrome counts a video's first frame as a largest-paint
    // candidate if it appears while the slide is wiping in; a frame first shown off screen never counts.
    const WAIT = 'tc-hero__wait';
    const enter = (slide) => {
      const v = videoFor(slide);
      if (v && !saveData && !reduce && (slide !== slides[0] || ready) && v.readyState < 2) { load(slide); v.classList.add(WAIT); }
      else play(slide);
    };

    hero.querySelectorAll('[data-tc-split]').forEach((h) => window.Tc?.splitLines(h));
    requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.remove('is-intro')));

    const stopClock = () => {
      if (timer !== null) remaining = Math.max(0, deadline - performance.now());
      clearTimeout(timer); timer = null;
    };
    const resetClock = () => {
      stopClock(); remaining = dur;
      const bar = tabs[index]?.querySelector('i');
      if (bar) { bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = ''; }
    };
    const setTabs = () => tabs.forEach((t, i) => {
      t.classList.toggle('is-active', i === index);
      t.classList.toggle('is-done', i < index);
      t.setAttribute('aria-selected', i === index);
      t.tabIndex = i === index ? 0 : -1;
    });
    const setTextAccess = () => texts.forEach((t, i) => {
      t.setAttribute('aria-hidden', String(i !== index));
      t.inert = i !== index;
      t.querySelectorAll('a').forEach((a) => { a.tabIndex = i === index ? 0 : -1; });
    });
    const paused = () => reduce || userPaused || focusWithin || !visible || document.hidden;
    const syncPlayback = () => {
      const stopped = paused();
      hero.classList.toggle('is-paused', stopped || busy);
      hero.classList.toggle('is-static-progress', reduce || !autoplay);
      if (pauseButton) {
        pauseButton.hidden = reduce;
        pauseButton.setAttribute('aria-pressed', String(userPaused));
        pauseButton.setAttribute('aria-label', userPaused ? pauseButton.dataset.resume : pauseButton.dataset.pause);
      }
      if (stopped) slides.forEach(pause);
      else play(slides[index]);
      if (stopped || busy || !autoplay || slides.length < 2) stopClock();
      else if (timer === null) {
        deadline = performance.now() + remaining;
        timer = setTimeout(() => { timer = null; go(index + 1); }, remaining);
      }
    };
    const edge = hero.querySelector('[data-tc-hero-edge]');
    const go = (next) => {
      next = (next + slides.length) % slides.length;
      if (busy) { queued = next; return; }
      if (next === index || slides.length < 2) return;
      stopClock();
      busy = true;
      const prev = index; index = next;
      const out = slides[prev], inn = slides[index];
      const tOut = texts[prev], tIn = texts[index];
      setTabs(); setTextAccess(); resetClock();
      if (reduce) {
        slides.forEach((s, i) => { s.classList.toggle('is-active', i === index); s.setAttribute('aria-hidden', String(i !== index)); });
        texts.forEach((t, i) => { t.classList.remove('is-out'); t.classList.toggle('is-active', i === index); });
        fill(inn); pause(out); busy = false; syncPlayback();
        return;
      }
      if (tOut) { tOut.classList.add('is-out'); tOut.classList.remove('is-active'); }
      enter(inn);
      out.classList.add('is-leaving'); out.classList.remove('is-active');
      inn.classList.add('is-entering'); inn.setAttribute('aria-hidden', 'false'); out.setAttribute('aria-hidden', 'true');
      if (edge) { edge.classList.remove('is-running'); void edge.offsetWidth; edge.classList.add('is-running'); }
      const textTimer = setTimeout(() => tIn?.classList.add('is-active'), textMs);
      const media = inn.querySelector('.tc-hero__media');
      let fallback;
      const onEnd = (e) => { if (e.target === media && e.animationName === 'tc-push-media') settle(); };
      const settle = () => {
        if (!busy) return;
        clearTimeout(textTimer); clearTimeout(fallback);
        media.removeEventListener('animationend', onEnd);
        tIn?.classList.add('is-active');
        inn.classList.remove('is-entering'); inn.classList.add('is-active');
        out.classList.remove('is-leaving'); pause(out);
        out.querySelectorAll('video.' + WAIT).forEach((v) => v.classList.remove(WAIT));
        if (tOut) { tOut.classList.add('is-reset'); tOut.classList.remove('is-out'); void tOut.offsetWidth; tOut.classList.remove('is-reset'); }
        load(slides[(index + 1) % slides.length]);
        busy = false; finishTransition = null;
        const pending = queued; queued = null;
        if (pending !== null && pending !== index) go(pending);
        else { resetClock(); syncPlayback(); }
      };
      finishTransition = settle;
      media.addEventListener('animationend', onEnd);
      // Fallback for detached sections or a browser that does not dispatch animationend.
      fallback = setTimeout(settle, transitionMs + 150);
      syncPlayback();
    };

    tabs.forEach((t, i) => {
      t.addEventListener('click', () => go(i));
      t.addEventListener('keydown', (e) => {
        let next;
        if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        if (e.key === 'ArrowLeft') next = (i + tabs.length - 1) % tabs.length;
        if (e.key === 'Home') next = 0;
        if (e.key === 'End') next = tabs.length - 1;
        if (next === undefined) return;
        e.preventDefault(); tabs[next].focus(); go(next);
      });
    });
    pauseButton?.addEventListener('click', () => { userPaused = !userPaused; syncPlayback(); });
    const syncFocus = () => {
      focusWithin = hero.contains(document.activeElement) && document.activeElement !== pauseButton;
      syncPlayback();
    };
    hero.addEventListener('focusin', syncFocus);
    hero.addEventListener('focusout', () => requestAnimationFrame(syncFocus));
    setTabs(); setTextAccess(); syncPlayback();

    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      document.documentElement.classList.toggle('tc-hero-in-view', visible);
      syncPlayback();
    }, { threshold: 0.2 });
    io.observe(hero);
    document.addEventListener('visibilitychange', syncPlayback);

    // The opening mask and the scroll transform use separate CSS properties.
    let ticking = false;
    const update = () => {
      if (reduce || hero.dataset.tcDriftEnabled === 'false') {
        stage.style.transform = ''; copy.style.transform = ''; copy.style.opacity = '';
        if (tabsWrap) tabsWrap.style.opacity = '';
      } else {
        const r = hero.getBoundingClientRect();
        const p = Math.min(Math.max(-r.top / r.height, 0), 1);
        stage.style.transform = `translate3d(0, ${(p * 18).toFixed(2)}%, 0) scale(${(1 + p * 0.08).toFixed(3)})`;
        copy.style.transform = `translate3d(0, ${(-p * 90).toFixed(1)}px, 0)`;
        copy.style.opacity = Math.max(0, 1 - p * 1.6).toFixed(3);
        if (tabsWrap) tabsWrap.style.opacity = Math.max(0, 1 - p * 2.2).toFixed(3);
      }
      ticking = false;
    };
    addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    addEventListener('resize', () => { slides.forEach(pause); syncPlayback(); update(); });
    addEventListener('pageshow', () => { syncPlayback(); update(); });
    motionQuery.addEventListener('change', () => {
      reduce = motionQuery.matches || hero.dataset.tcMotion === 'false';
      finishTransition?.(); syncPlayback(); update();
    });
    update();
  });
})();
